import type { JSONContent } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';

/**
 * The reply is a plain string with line breaks (PRD 4.3). The editor holds it as
 * one paragraph per line, an empty paragraph being a blank line, so the string
 * that is autosaved and sent is exactly the lines joined by "\n".
 */

export function normalizeNewlines(text: string): string {
  return text.replace(/\r\n?/g, '\n');
}

/** The editor document for a plain string. */
export function textToDoc(text: string): JSONContent {
  return {
    type: 'doc',
    content: normalizeNewlines(text)
      .split('\n')
      .map((line) =>
        line
          ? { type: 'paragraph', content: [{ type: 'text', text: line }] }
          : { type: 'paragraph' }
      ),
  };
}

/** The plain string of an editor document. */
export function docToText(doc: ProseMirrorNode): string {
  return doc.textBetween(0, doc.content.size, '\n');
}

/**
 * Position in the document of a character offset in the plain string. Every
 * paragraph boundary takes two positions (close, open) but one character, so
 * each "\n" before the offset adds one.
 */
export function offsetToPos(text: string, offset: number): number {
  const clamped = Math.max(0, Math.min(offset, text.length));
  const lineBreaks = text.slice(0, clamped).split('\n').length - 1;
  return 1 + clamped + lineBreaks;
}

/** Character offset in the plain string of a document position. */
export function posToOffset(doc: ProseMirrorNode, pos: number): number {
  return doc.textBetween(0, pos, '\n').length;
}
